import { Component, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { MerchantChangeDetailComponent } from './merchant-change-detail/merchant-change-detail.component';
import { RiskAlertDetailComponent } from './risk-alert-detail/risk-alert-detail.component';
import { PreLaunchEvmDetailComponent } from './pre-launch-evm-detail/pre-launch-evm-detail.component';
import {
  MerchantChangeTaskDetail,
  RiskAlertTaskDetail,
  TaskDetail,
  DelegationTaskDetail,
  MediaFile,
  PreLaunchEvmBalanceDetail,
  Task,
  ResolveTaskDto,
} from '../../../../models/task.model';

import { Transaction } from '@olv/ui-shared/models/merchant-transaction.model';
import {
  RiskLevelEnum,
  TaskPriorityEnum,
  TaskResolutionEnum,
  TaskStatusEnum,
  TaskTypeEnum,
} from '@olv/ui-shared/enums/task.enums';
import { RiskGroupShortCodeEnum } from '@olv/ui-shared/enums/risk-group-short-code.enum';
import { TransactionDetailsComponent } from '@olv/ui-shared/ui-shared/transaction-details/transaction-details.component';
import { MerchantWhitelistedWalletDetailsComponent } from '@olv/ui-shared/features/dashboard/merchant-whitelisted-wallets/merchant-whitelisted-wallet-details/merchant-whitelisted-wallet-details.component';
import type { WhitelistedWallet } from '@olv/ui-shared/features/dashboard/merchant-whitelisted-wallets/merchant-whitelisted-wallets.models';
import { TaskService } from '@olv/ui-shared/services/task.service';
import { formatDateStandard } from '@olv/ui-shared/utils/dateFormat.utils';
import { OtpVerificationDialogComponent } from '@olv/ui-shared/ui-shared/otp-verification-dialog/otp-verification-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastService } from '@olv/ui-shared/ui-shared/toast/toast.service';
import { ButtonComponent, ButtonState, toButtonState } from '@olv/ui-shared/ui-shared/button/button.component';

@Component({
  selector: 'app-task-details',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    FormsModule,
    MerchantChangeDetailComponent,
    RiskAlertDetailComponent,
    PreLaunchEvmDetailComponent,
    TransactionDetailsComponent,
    MerchantWhitelistedWalletDetailsComponent,
    ButtonComponent,
  ],
  templateUrl: './task-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./task-details.component.scss'],
})
export class TaskDetailsComponent {
  private taskService = inject(TaskService);
  private toastr = inject(ToastService);

  @Input() task: Task | null = null;
  @Input() taskDetail: TaskDetail | null = null;
  @Output() closed = new EventEmitter<void>();
  @Output() taskUpdated = new EventEmitter<Task>();

  comment = '';
  resolving = false;
  errorMessage = '';

  protected readonly formatDateStandard = formatDateStandard;

  private dialog = inject(MatDialog);

  get processingButtonState(): ButtonState {
    return toButtonState(this.resolving, false);
  }

  onClose(): void {
    this.closed.emit();
  }

  onApprove(): void {
    // For whitelist tasks, require OTP verification
    if (this.isWhitelistTask()) {
      this.openOTP();
    } else {
      this.resolveTask(TaskResolutionEnum.Approved);
    }
  }

  onReject(): void {
    this.resolveTask(TaskResolutionEnum.Rejected);
  }

  onAcknowledge(): void {
    if (!this.task || this.isTaskHistory()) {
      return;
    }

    const task = this.task as Task;
    this.resolving = true;
    this.errorMessage = '';

    this.taskService
      .acknowledgeTask(task.id, { comment: this.comment || undefined })
      .pipe(finalize(() => (this.resolving = false)))
      .subscribe({
        next: () => {
          this.taskUpdated.emit();
          this.showSuccess('Task acknowledged successfully');
          this.onClose();
        },
        error: (error) => {
          this.errorMessage = error.error?.message || 'Failed to acknowledge task';
          this.showError(this.errorMessage);
        },
      });
  }

  private resolveTask(
    resolution: TaskResolutionEnum,
    otp?: string,
    dialogRef?: MatDialogRef<OtpVerificationDialogComponent>,
  ): void {
    if (!this.task || this.isTaskHistory()) {
      return;
    }

    const task = this.task as Task;
    this.resolving = true;
    this.errorMessage = '';

    if (dialogRef) {
      dialogRef.componentInstance.setLoadingState(true);
      dialogRef.disableClose = true;
    }

    const resolveDto: ResolveTaskDto = {
      resolution,
      comment: this.comment || undefined,
      attachmentIds: [], // Could be extended to support file uploads
      totp: otp, // Add OTP support
    };

    this.taskService
      .resolveTask(task.id, resolveDto)
      .pipe(
        finalize(() => {
          this.resolving = false;
          if (dialogRef) {
            dialogRef.disableClose = false;
            dialogRef.componentInstance.setLoadingState(false);
          }
        }),
      )
      .subscribe({
        next: () => {
          if (dialogRef) dialogRef.close();
          this.taskUpdated.emit();
          this.showSuccess(`Task ${resolution === TaskResolutionEnum.Approved ? 'approved' : 'rejected'} successfully`);
          this.onClose();
        },
        error: (error) => {
          if (error.isOtpError && dialogRef) {
            dialogRef.componentInstance.otpForm.reset();
            dialogRef.componentInstance.focusInput(0);
            dialogRef.componentInstance.setErrorState(error.error?.message);
            return;
          }

          if (dialogRef) dialogRef.close();

          this.errorMessage =
            error.error?.message ||
            `Failed to ${resolution === TaskResolutionEnum.Approved ? 'approve' : 'reject'} task`;
          this.showError(this.errorMessage);
        },
      });
  }

  private showSuccess(message: string): void {
    this.toastr.success(message);
  }

  private showError(message: string): void {
    this.toastr.error(message);
  }

  getTaskIcon(type: TaskTypeEnum): string {
    const iconMap: Record<TaskTypeEnum, string> = {
      [TaskTypeEnum.Whitelist]: 'verified_user',
      [TaskTypeEnum.Withdrawal]: 'account_balance_wallet',
      [TaskTypeEnum.Merchant]: 'business',
      [TaskTypeEnum.Deposit]: 'account_balance',
      [TaskTypeEnum.Risk]: 'warning',
      [TaskTypeEnum.SystemWhitelist]: 'admin_panel_settings',
      [TaskTypeEnum.SystemWithdrawal]: 'settings_backup_restore',
      [TaskTypeEnum.SystemDeposit]: 'settings_input_antenna',
      [TaskTypeEnum.SystemRisk]: 'security',
      [TaskTypeEnum.TransactionAdjustment]: 'account_balance_wallet',
      [TaskTypeEnum.DepositReversal]: 'account_balance_wallet',
      [TaskTypeEnum.UnexpectedDeposit]: 'account_balance',
      [TaskTypeEnum.PaymentDepositAcknowledge]: 'account_balance',
      [TaskTypeEnum.DelegationAllowanceDepleted]: 'battery_alert',
      [TaskTypeEnum.DelegationReserveDepleted]: 'error',
      [TaskTypeEnum.DelegationAllowanceTopUpOverSpend]: 'trending_up',
      [TaskTypeEnum.DelegationCapacityDrift]: 'sync_problem',
      [TaskTypeEnum.ExchangeRateRequired]: 'price_change',
      [TaskTypeEnum.PreLaunchEvmDeposit]: 'account_balance_wallet',
      [TaskTypeEnum.DelegationAllowanceUnderfunded]: 'trending_down',
    };
    return iconMap[type] || 'task';
  }

  getTaskField(field: keyof Task): string | undefined {
    if (!this.task) return undefined;

    // Handle TaskHistory vs Task field mapping
    if (this.isTaskHistory()) {
      const historyTask = this.task as Task;
      switch (field) {
        case 'initiatedByDisplayName':
          return historyTask.resolvedByDisplayName;
        case 'id':
          return historyTask.id;
        case 'title':
          return historyTask.title;
        case 'type':
          return historyTask.type.toString();
        case 'priority':
          return historyTask.priority.toString();
        case 'status':
          return historyTask.status.toString();
        default:
          return undefined;
      }
    } else {
      const task = this.task as Task;
      switch (field) {
        case 'initiatedByDisplayName':
          return task.initiatedByDisplayName;
        case 'id':
          return task.id;
        case 'title':
          return task.title;
        case 'type':
          return task.type.toString();
        case 'priority':
          return task.priority.toString();
        case 'status':
          return task.status.toString();
        // case 'walletAddress':
        //   return task.walletAddress;
        default:
          return undefined;
      }
    }
  }

  getTaskProperty(
    property: keyof Task,
  ):
    | string
    | boolean
    | Date
    | string[]
    | MediaFile[]
    | TaskStatusEnum
    | TaskTypeEnum
    | TaskPriorityEnum
    | TaskResolutionEnum
    | RiskLevelEnum
    | DelegationTaskDetail
    | PreLaunchEvmBalanceDetail
    | undefined {
    if (!this.task) return undefined;

    // Only return property if it exists on Task (not TaskHistory)
    if (!this.isTaskHistory()) {
      const task = this.task as Task;
      // Exclude whiteListedWalletDetail from being returned as a property
      if (property === 'whitelistedWalletDetail') return undefined;
      if (property === 'transactionDetail') return undefined;
      return task[property];
    }

    return undefined;
  }

  getTaskDate(): string {
    if (!this.task) return '-';

    // API tasks always include resolvedAt (often null), so property presence cannot identify history.
    if (this.task.resolvedAt) return this.formatDateStandard(this.task.resolvedAt);
    return this.task.dueDate ? this.formatDateStandard(this.task.dueDate) : '-';
  }

  isTaskHistory(): boolean {
    if (!this.task) return false;

    // Check if this is a TaskHistory object (has resolvedAt property set)
    // or if it's a completed/cancelled Task.
    // API tasks carry resolvedAt: null (see getTaskDate), so only an actual resolution date makes it history —
    // otherwise a pending task loses its actions and onAcknowledge() returns without sending anything.
    const isHistoryObject = this.task.resolvedAt !== null && this.task.resolvedAt !== undefined;
    const isCompletedTask =
      this.task.status === TaskStatusEnum.Completed || this.task.status === TaskStatusEnum.Cancelled;

    return isHistoryObject || isCompletedTask;
  }

  // Task type checking methods
  isMerchantTask(): boolean {
    return this.task?.type === TaskTypeEnum.Merchant;
  }

  isWhitelistTask(): boolean {
    return this.task?.type === TaskTypeEnum.Whitelist || this.task?.type === TaskTypeEnum.SystemWhitelist;
  }

  isRiskTask(): boolean {
    return this.task?.type === TaskTypeEnum.Risk || this.task?.type === TaskTypeEnum.SystemRisk;
  }

  isWithdrawalTask(): boolean {
    return (
      this.task?.type === TaskTypeEnum.Withdrawal ||
      this.task?.type === TaskTypeEnum.SystemWithdrawal ||
      this.task?.type === TaskTypeEnum.TransactionAdjustment ||
      this.task?.type === TaskTypeEnum.DepositReversal
    );
  }

  isDepositTask(): boolean {
    return (
      this.task?.type === TaskTypeEnum.Deposit ||
      this.task?.type === TaskTypeEnum.SystemDeposit ||
      this.task?.type === TaskTypeEnum.UnexpectedDeposit ||
      this.task?.type === TaskTypeEnum.PaymentDepositAcknowledge
    );
  }

  // these all render through the transaction panel, which carries their own actions
  isTransactionTask(): boolean {
    return this.isWithdrawalTask() || this.isDepositTask() || this.isExchangeRateRequiredTask();
  }

  getResolvedAtText(): string {
    const resolvedAt = this.getTaskResolutionDate();
    return resolvedAt ? this.formatDateStandard(resolvedAt) : '-';
  }

  hasActions(): boolean {
    return (
      !this.isTaskHistory() &&
      !this.isWhitelistTask() &&
      !this.isMerchantTask() &&
      !this.isRiskTask() &&
      !this.isTransactionTask() &&
      !this.isPreLaunchEvmTask()
    );
  }

  isDelegationTask(): boolean {
    return (
      this.task?.type === TaskTypeEnum.DelegationAllowanceDepleted ||
      this.task?.type === TaskTypeEnum.DelegationReserveDepleted ||
      this.task?.type === TaskTypeEnum.DelegationAllowanceTopUpOverSpend ||
      this.task?.type === TaskTypeEnum.DelegationCapacityDrift ||
      this.task?.type === TaskTypeEnum.DelegationAllowanceUnderfunded
    );
  }

  isPreLaunchEvmTask(): boolean {
    return this.task?.type === TaskTypeEnum.PreLaunchEvmDeposit;
  }

  getPreLaunchEvmDetail(): PreLaunchEvmBalanceDetail | undefined {
    return (this.task as Task | null)?.preLaunchEvmBalanceDetail;
  }

  getDelegationDetail(): DelegationTaskDetail | undefined {
    return (this.task as Task | null)?.delegationDetail;
  }

  getResourceTypeLabel(): string {
    switch (this.getDelegationDetail()?.resourceType) {
      case 0:
        return 'Bandwidth';
      case 1:
        return 'Energy';
      case 2:
        return 'Tron Power';
      default:
        return '';
    }
  }

  getRiskGroupLabel(): string | undefined {
    const riskGroup = this.getDelegationDetail()?.riskGroupShortCode;
    const labels: Record<RiskGroupShortCodeEnum, string> = {
      [RiskGroupShortCodeEnum.LowExchange]: 'Low Exchange',
      [RiskGroupShortCodeEnum.Low]: 'Low',
      [RiskGroupShortCodeEnum.Medium]: 'Medium',
      [RiskGroupShortCodeEnum.High]: 'High',
      [RiskGroupShortCodeEnum.Severe]: 'Severe',
      [RiskGroupShortCodeEnum.NotAssessed]: 'Not Assessed',
    };
    return riskGroup === null || riskGroup === undefined ? undefined : labels[riskGroup];
  }

  getTaskDescription(): string {
    return this.task?.description.replace(/\s*\[[a-z-]+:[^\]]*\]\s*$/, '').trim() ?? '';
  }

  isExchangeRateRequiredTask(): boolean {
    return this.task?.type === TaskTypeEnum.ExchangeRateRequired;
  }

  isUnexpectedDepositTask(): boolean {
    return this.task?.type === TaskTypeEnum.UnexpectedDeposit;
  }

  isSystemTask(): boolean {
    return (
      this.task?.type === TaskTypeEnum.SystemWhitelist ||
      this.task?.type === TaskTypeEnum.SystemWithdrawal ||
      this.task?.type === TaskTypeEnum.SystemDeposit ||
      this.task?.type === TaskTypeEnum.SystemRisk
    );
  }

  // Get typed task details
  getMerchantDetail(): MerchantChangeTaskDetail | undefined {
    return this.isMerchantTask() && this.taskDetail ? (this.taskDetail as MerchantChangeTaskDetail) : undefined;
  }

  getWhitelistDetail(): WhitelistedWallet | undefined {
    if (!this.task || !this.isWhitelistTask()) return undefined;
    const task = this.task as Task;
    const detail = task.whitelistedWalletDetail;
    if (!detail) return undefined;
    // Convert date string to Date for compatibility with WhitelistedWallet
    return {
      ...detail,
      date: detail.date ? new Date(detail.date) : new Date(0), // fallback to epoch if undefined
      riskLevel: this.mapBackendRiskLevelToWalletRiskLevel(detail.riskLevel),
      status: this.mapBackendStatusToWalletStatus(detail.status),
      resolvedByDisplayName: task.resolvedByDisplayName,
      resolvedAt: task.resolvedAt,
      resolution: task.resolution,
      preparedBy: task.initiatedByDisplayName,
      canApproveOrReject: detail.canApproveOrReject,
    };
  }

  private mapBackendStatusToWalletStatus(status: string): WhitelistedWallet['status'] {
    const statusMap: Record<string, WhitelistedWallet['status']> = {
      Pending: 'Pending',
      Approved: 'Approved',
      Rejected: 'Rejected',
      Revoked: 'Revoked',
    };
    return statusMap[status] || 'Pending';
  }

  getRiskDetail(): RiskAlertTaskDetail | undefined {
    return this.isRiskTask() && this.taskDetail ? (this.taskDetail as RiskAlertTaskDetail) : undefined;
  }

  getWithdrawalAsTransaction(): Transaction | undefined {
    if (!this.task || !this.isTransactionTask()) return undefined;

    const task = this.task as Task;
    const detail = (this.taskDetail as Transaction | undefined) ?? task.transactionDetail;
    if (!detail) return undefined;

    return {
      ...detail,
      id: detail.id,
      createdAt: detail.date,
      preparedBy: task.initiatedByDisplayName,
      hasAwaitingApproval: detail.hasAwaitingApproval || false,
      canSpeedup: detail.canSpeedup || false,
      canCancel: detail.canCancel || false,
      allowsAdjustment: detail.allowsAdjustment || false,
    };
  }

  private mapBackendRiskLevelToWalletRiskLevel(riskLevel: string | RiskLevelEnum): WhitelistedWallet['riskLevel'] {
    // Handle both string and enum values
    if (typeof riskLevel === 'number') {
      // It's an enum value
      return this.mapTaskRiskLevelToWalletRiskLevel(riskLevel as RiskLevelEnum);
    }

    // It's a string value
    const riskLevelMap: Record<string, WhitelistedWallet['riskLevel']> = {
      NotAssessed: 'NotAssessed',
      Unknown: 'Unknown',
      Low: 'Low',
      Medium: 'Medium',
      High: 'High',
      Severe: 'Severe',
    };
    return riskLevelMap[riskLevel] || 'NotAssessed';
  }

  private getWalletNameFromTask(task: Task): string {
    // First try to use the task title if it's meaningful
    if (task.title && !task.title.includes('Task')) {
      return task.title;
    }

    // Otherwise generate a wallet name from the address
    const address = (task as any).walletAddress;
    if (typeof address === 'string' && address.length > 0) {
      return `Wallet ${address.substring(0, 8)}...`;
    }

    return 'Whitelisted Wallet';
  }

  private mapTaskRiskLevelToWalletRiskLevel(riskLevel?: RiskLevelEnum): WhitelistedWallet['riskLevel'] {
    if (!riskLevel) return 'NotAssessed';

    const riskLevelMap: Record<RiskLevelEnum, WhitelistedWallet['riskLevel']> = {
      [RiskLevelEnum.Unsupported]: 'Unsupported',
      [RiskLevelEnum.NotAssessed]: 'NotAssessed',
      [RiskLevelEnum.Unknown]: 'Unknown',
      [RiskLevelEnum.Low]: 'Low',
      [RiskLevelEnum.Medium]: 'Medium',
      [RiskLevelEnum.High]: 'High',
      [RiskLevelEnum.Severe]: 'Severe',
    };
    return riskLevelMap[riskLevel] || 'NotAssessed';
  }

  private getResolutionBadge(): { cssClass: string; icon: string } | undefined {
    if (!this.task || !this.isTaskHistory()) return undefined;

    switch ((this.task as Task).resolution) {
      case TaskResolutionEnum.Approved:
        return { cssClass: 'status-completed', icon: 'check' };
      case TaskResolutionEnum.Rejected:
      case TaskResolutionEnum.Cancelled:
        return { cssClass: 'status-cancelled', icon: 'close' };
      default:
        return undefined;
    }
  }

  getBadgeText(): string {
    return this.getTaskResolution() ?? this.getStatusText();
  }

  getStatusClass(): string {
    if (!this.task) return 'status-pending';

    const resolutionBadge = this.getResolutionBadge();
    if (resolutionBadge) return resolutionBadge.cssClass;

    if (this.isTaskHistory()) {
      // TaskHistory status
      const historyTask = this.task as Task;
      switch (historyTask.status) {
        case TaskStatusEnum.Completed:
          return 'status-completed';
        case TaskStatusEnum.Cancelled:
          return 'status-cancelled';
        default:
          return 'status-pending';
      }
    } else {
      // Task status
      const task = this.task as Task;
      switch (task.status) {
        case TaskStatusEnum.Completed:
          return 'status-completed';
        case TaskStatusEnum.Pending:
          return 'status-pending';
        case TaskStatusEnum.Cancelled:
          return 'status-cancelled';
        case TaskStatusEnum.Overdue:
          return 'status-overdue';
        default:
          return 'status-pending';
      }
    }
  }

  getStatusText(): string {
    if (!this.task) return 'Pending';

    if (this.isTaskHistory()) {
      // TaskHistory status
      const historyTask = this.task as Task;
      switch (historyTask.status) {
        case TaskStatusEnum.Completed:
          return 'Completed';
        case TaskStatusEnum.Cancelled:
          return 'Cancelled';
        default:
          return 'Pending';
      }
    } else {
      // Task status
      const task = this.task as Task;
      switch (task.status) {
        case TaskStatusEnum.Completed:
          return 'Completed';
        case TaskStatusEnum.Pending:
          return 'Pending';
        case TaskStatusEnum.Cancelled:
          return 'Cancelled';
        case TaskStatusEnum.Overdue:
          return 'Overdue';
        default:
          return 'Pending';
      }
    }
  }

  getStatusIcon(): string {
    if (!this.task) return 'access_time';

    const resolutionBadge = this.getResolutionBadge();
    if (resolutionBadge) return resolutionBadge.icon;

    if (this.isTaskHistory()) {
      const historyTask = this.task as Task;
      switch (historyTask.status) {
        case TaskStatusEnum.Completed:
          return 'check';
        case TaskStatusEnum.Cancelled:
          return 'close';
        default:
          return 'access_time';
      }
    } else {
      const task = this.task as Task;
      switch (task.status) {
        case TaskStatusEnum.Completed:
          return 'check';
        case TaskStatusEnum.Pending:
          return 'access_time';
        case TaskStatusEnum.Cancelled:
          return 'close';
        case TaskStatusEnum.Overdue:
          return 'error';
        default:
          return 'access_time';
      }
    }
  }

  getTaskResolution(): string | undefined {
    if (this.isTaskHistory()) {
      const historyTask = this.task as Task;

      switch (historyTask.resolution) {
        case TaskResolutionEnum.Approved:
          return 'Approved';
        case TaskResolutionEnum.Rejected:
          return 'Rejected';
        case TaskResolutionEnum.Cancelled:
          return 'Cancelled';
        default:
          return undefined;
      }
    }

    return undefined;
  }

  getTaskResolutionDate(): Date | undefined {
    if (this.isTaskHistory()) {
      const historyTask = this.task as Task;
      return historyTask.resolvedAt;
    }
    return undefined;
  }

  getTaskAttachments(): MediaFile[] {
    if (!this.isTaskHistory() && this.task) {
      const task = this.task as Task;
      // removed reference to attachments (no longer in Task interface)
      return [];
    }
    return [];
  }

  openOTP(): void {
    const dialogRef = this.dialog.open(OtpVerificationDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Approve Task',
      },
    });

    const sub = dialogRef.componentInstance.submitOtp.subscribe((otp: string) => {
      this.resolveTask(TaskResolutionEnum.Approved, otp, dialogRef);
    });

    dialogRef.afterClosed().subscribe(() => {
      sub.unsubscribe();
    });
  }

  onTaskRefresh(): void {
    // Emit the updated task to refresh the parent component
    if (this.task && !this.isTaskHistory()) {
      this.taskUpdated.emit();
    }
  }

  canResolveTask() {
    return this.task?.status === TaskStatusEnum.Pending;
  }

  getTaskInitiatedBy(): string | undefined {
    return this.task?.initiatedByDisplayName;
  }

  getTaskResolvedBy(): string | undefined {
    if (this.isTaskHistory()) {
      const historyTask = this.task as Task;
      return historyTask.resolvedByDisplayName;
    }
    return undefined;
  }
}
